import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Users, 
  DollarSign, 
  Clock, 
  CheckCircle2, 
  Gift,
  Search,
  RefreshCw,
  ArrowUpDown,
  UserPlus,
  Award,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function AdminReferrals() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [topReferrers, setTopReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReferrals, setSelectedReferrals] = useState([]);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  const backendUrl = getBackendUrl();

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch referrals
      const statusParam = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const referralsRes = await axios.get(
        `${backendUrl}/api/admin/referrals${statusParam}`,
        { headers }
      );
      setReferrals(referralsRes.data.referrals || []);

      // Fetch summary
      const summaryRes = await axios.get(
        `${backendUrl}/api/admin/referrals/summary`,
        { headers }
      );
      setSummary(summaryRes.data.summary);
      setTopReferrers(summaryRes.data.top_referrers || []);

    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (selectedReferrals.length === 0) {
      toast.error('Please select referrals to mark as paid');
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('access_token');
      
      await axios.post(
        `${backendUrl}/api/admin/referrals/mark-paid`,
        { referral_ids: selectedReferrals },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${selectedReferrals.length} referral(s) marked as paid`);
      setSelectedReferrals([]);
      setShowPayDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error marking referrals as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to update referrals');
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelectReferral = (id) => {
    setSelectedReferrals(prev => 
      prev.includes(id) 
        ? prev.filter(r => r !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const completedReferrals = filteredReferrals.filter(r => r.status === 'completed');
    if (selectedReferrals.length === completedReferrals.length) {
      setSelectedReferrals([]);
    } else {
      setSelectedReferrals(completedReferrals.map(r => r.id));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'completed':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800"><CheckCircle2 className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'rewarded':
        return <Badge variant="secondary" className="bg-green-100 text-green-800"><Gift className="w-3 h-3 mr-1" />Rewarded</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredReferrals = referrals.filter(r => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      r.referrer?.name?.toLowerCase().includes(search) ||
      r.referrer?.email?.toLowerCase().includes(search) ||
      r.referee?.name?.toLowerCase().includes(search) ||
      r.referee?.email?.toLowerCase().includes(search) ||
      r.referral_code?.toLowerCase().includes(search)
    );
  });

  const completedCount = referrals.filter(r => r.status === 'completed').length;

  if (!user || user.role !== 'admin') {
    return (
      <DashboardLayout role={user?.role} title="Referrals" user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Admin access required</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Referrals" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Referrals" user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6" />
              Referral Management
            </h2>
            <p className="text-muted-foreground">Track and manage all referrals</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            {completedCount > 0 && (
              <Button onClick={() => setShowPayDialog(true)} disabled={selectedReferrals.length === 0}>
                <DollarSign className="mr-2 h-4 w-4" />
                Mark as Paid ({selectedReferrals.length})
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Total Referrals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.total_referrals}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-500" />
                  Pending Signup
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{summary.pending_signup}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-blue-500" />
                  Completed (Unpaid)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{summary.completed_unpaid}</div>
                <p className="text-xs text-muted-foreground">${summary.pending_payment_amount} pending</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Gift className="h-4 w-4 text-green-500" />
                  Rewarded (Paid)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{summary.rewarded_paid}</div>
                <p className="text-xs text-muted-foreground">${summary.total_paid_amount} paid</p>
              </CardContent>
            </Card>
            <Card className="bg-primary/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-primary" />
                  Potential Liability
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary">${summary.potential_liability}</div>
                <p className="text-xs text-muted-foreground">${summary.reward_per_referral}/referral</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="referrals" className="space-y-4">
          <TabsList>
            <TabsTrigger value="referrals" className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4" />
              All Referrals
            </TabsTrigger>
            <TabsTrigger value="top-referrers" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Top Referrers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Referrals</CardTitle>
                    <CardDescription>View who referred whom</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="rewarded">Rewarded</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredReferrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground" />
                    <p className="mt-4 text-lg font-medium">No referrals found</p>
                    <p className="text-muted-foreground">
                      {statusFilter !== 'all' ? 'Try changing the filter' : 'Referrals will appear here'}
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          {completedCount > 0 && (
                            <Checkbox
                              checked={selectedReferrals.length === filteredReferrals.filter(r => r.status === 'completed').length && selectedReferrals.length > 0}
                              onCheckedChange={toggleSelectAll}
                            />
                          )}
                        </TableHead>
                        <TableHead>Referrer</TableHead>
                        <TableHead>Referee</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reward</TableHead>
                        <TableHead>Signup Date</TableHead>
                        <TableHead>Completion Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReferrals.map((referral) => (
                        <TableRow key={referral.id} data-testid={`referral-row-${referral.id}`}>
                          <TableCell>
                            {referral.status === 'completed' && (
                              <Checkbox
                                checked={selectedReferrals.includes(referral.id)}
                                onCheckedChange={() => toggleSelectReferral(referral.id)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{referral.referrer?.name || 'Unknown'}</p>
                              <p className="text-sm text-muted-foreground">{referral.referrer?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {referral.referee ? (
                              <div>
                                <p className="font-medium">{referral.referee.name || 'Unknown'}</p>
                                <p className="text-sm text-muted-foreground">{referral.referee.email}</p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-muted-foreground italic">Not signed up</p>
                                <p className="text-sm text-muted-foreground">{referral.referee_email_invited}</p>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">{referral.referral_code}</code>
                          </TableCell>
                          <TableCell>{getStatusBadge(referral.status)}</TableCell>
                          <TableCell className="font-medium">${referral.reward_amount}</TableCell>
                          <TableCell>
                            {referral.signup_date 
                              ? new Date(referral.signup_date).toLocaleDateString()
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            {referral.completion_date 
                              ? new Date(referral.completion_date).toLocaleDateString()
                              : '-'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="top-referrers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Top Referrers
                </CardTitle>
                <CardDescription>Users with the most referrals</CardDescription>
              </CardHeader>
              <CardContent>
                {topReferrers.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 mx-auto text-muted-foreground" />
                    <p className="mt-4 text-muted-foreground">No referrers yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rank</TableHead>
                        <TableHead>Referrer</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead className="text-center">Completed</TableHead>
                        <TableHead className="text-center">Rewarded</TableHead>
                        <TableHead className="text-right">Earnings Paid</TableHead>
                        <TableHead className="text-right">Earnings Pending</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topReferrers.map((referrer, index) => (
                        <TableRow key={referrer.id}>
                          <TableCell>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-800' :
                              index === 1 ? 'bg-gray-100 text-gray-800' :
                              index === 2 ? 'bg-orange-100 text-orange-800' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {index + 1}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{referrer.name || 'Unknown'}</p>
                              <p className="text-sm text-muted-foreground">{referrer.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-bold">{referrer.total_referrals}</TableCell>
                          <TableCell className="text-center text-blue-600">{referrer.completed_referrals}</TableCell>
                          <TableCell className="text-center text-green-600">{referrer.rewarded_referrals}</TableCell>
                          <TableCell className="text-right text-green-600">${referrer.earnings_paid.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-yellow-600">${referrer.earnings_pending.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Mark as Paid Dialog */}
        <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Referrals as Paid</DialogTitle>
              <DialogDescription>
                Are you sure you want to mark {selectedReferrals.length} referral(s) as paid/rewarded?
                This indicates that payment has been made to the referrer(s).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Total amount: <span className="font-bold text-foreground">
                  ${selectedReferrals.length * (summary?.reward_per_referral || 50)}
                </span>
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPayDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleMarkAsPaid} disabled={processing}>
                {processing ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Confirm Payment
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
